import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  getBrowserThirdPartyCookiesHintId,
  getBrowserVendorHelpUrl,
} from '@/lib/utils/browser-third-party-cookies-hint';
import { ExternalLink, Unplug } from 'lucide-react';

/** Affiché quand `getAccessTokenSilently` échoue avec OAuth `error=login_required` (ex. cookies tiers bloqués). */
export function AuthThirdPartyCookiesMessage() {
  const { t } = useTranslation();

  const hintId = useMemo(() => getBrowserThirdPartyCookiesHintId(), []);
  const vendorHelpUrl = getBrowserVendorHelpUrl(hintId);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 text-center">
          <div
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted"
            aria-hidden
          >
            <Unplug className="h-8 w-8 text-muted-foreground" />
          </div>
          <CardDescription className="text-base text-foreground">
            {t('page.redirect.third_party_cookies')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p
            className={cn(
              'text-left text-sm leading-snug text-muted-foreground',
              'max-h-[7.5rem] overflow-y-auto pr-1'
            )}
          >
            {t(`page.redirect.third_party_cookies_hint_${hintId}`)}
          </p>

          {vendorHelpUrl ? (
            <div className="flex flex-col gap-2 border-t border-border pt-3 text-left text-sm">
              <a
                href={vendorHelpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 font-medium text-primary underline-offset-4 hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-80" />
                {t('page.redirect.third_party_cookies_link_vendor')}
              </a>
            </div>
          ) : null}

          <Button
            type="button"
            className="w-full"
            variant="default"
            onClick={() => window.location.reload()}
          >
            {t('page.redirect.third_party_cookies_retry')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
